import domain from '@/server-config/domain.config'
import { showLoadingToast, showDialog } from 'vant'
import type { ToastWrapperInstance } from 'vant/lib/toast/types'
import { getTypeof, throttle } from '@/plugins/utils'
import md5 from 'js-md5'
import { v4 as uuidv4 } from 'uuid'

interface HTTP {
  post: (reqParams: REQ_PARAMS) =>  any,
  get: (reqParams: REQ_PARAMS) => any,
  put: (reqParams: REQ_PARAMS) => any,
  delete: (reqParams: REQ_PARAMS) => any,
  ajax: (reqParams: REQ_AJAX_PARAMS) => any,
  patch: (reqParams: REQ_PARAMS) => any,
}

interface REQ_PARAMS {
  /** 请求域名  */
  baseURL?: string,
  /** 请求接口路径 */
  url: string,
  /** 请求参数可传递对象 */
  query?: any,
  /** 请求参数可传递对象 */
  params?: any,
  /** body请求参数体 */
  body?: any,
  /** 请求头部参数 */
  // headers?: HeadersInit,
  headers?: any,
  // headers?: any,
  /** 是否服务端请求, 默认false */
  server?: boolean,
  /** 是否显示 loading */
  loading?: boolean,
  transform?: any,
  toastError?: boolean,
  /** Whether a non-success business code should reject the request. */
  rejectBusinessError?: boolean
}

interface REQ_AJAX_PARAMS extends REQ_PARAMS {
  /** 请求类型 */
  method: string
}
interface RequestClientCtx {
  route: ReturnType<typeof useRoute>
  router: ReturnType<typeof useRouter>
}

/**
 * 请求头错误处理
 * @param error 
 * @returns 
 */
const handleResponseError = (
  error: any,
  toastError: boolean = true,
  clientCtx?: RequestClientCtx,
  isBusinessError = false,
  requestUrl?: string,
) => {
  const errorObj = error?._data || error?.response?._data ||  null

  if (error) {
    switch (error.status) {
      case 401:
        if (!process.client || !clientCtx) {
          break
        }
        if(useIsAndroid().value) {
          // 关闭当前 webview，进入 App 登录页面（app登录后需要重新打开该页面）
          window.androidWebObject.forceToLogin()
        } else if(useIsFlutterApp().value) {
          window.flutter_inappwebview.callHandler('forceToLogin')
        } else {
          // 跳转到登录界面
          const path = clientCtx.route.fullPath
          // 如果是登录页面报 401,重定向指向首页
          if(/\/login/.test(path)) {
            break
          }

          void clientCtx.router
            .push(`/login?redirect=${encodeURI(path)}`)
            .catch(() => undefined)
        }
        // err('登录状态已过期，需要重新登录')
        break
      default:
        if(error._data && toastError) {
          const error_msg = error._data.message || error._data.msg

          // HTTP CODE 400 - 499 弹出提示信息
          if((isBusinessError || (error.status >= 400 && error.status < 500)) && error_msg) {
            // code 5004 商品异常下架 code 码
            error._data.code !== 5004 && showToast({message: error_msg, duration: 5000})
          }

          const code = Number(errorObj?.code)
          const expectedGoodsDetailFailure = requestUrl === '/goods-detail'
            && (error.status === 429 || [1015, 5004, 40000].includes(code))

          if (!expectedGoodsDetailFailure) {
            console.error('Request Error: ', error_msg)
          }
        }
        
        break
    }
  } else {
    // err('请求超时，服务器无响应！')
    console.error('Request Error: Request timed out, please try again later.')
  }

  return errorObj
}

const onResponseError = (
  error: any,
  toastError: boolean = true,
  isServer = false,
  clientCtx?: RequestClientCtx,
  isBusinessError = false,
  requestUrl?: string,
) => {
  const errorObj = handleResponseError(
    error,
    toastError,
    clientCtx,
    isBusinessError,
    requestUrl
  )
  return isServer && process.server
    ? Promise.reject(new Error(JSON.stringify(errorObj || '{}')))
    : Promise.reject(errorObj)
}

const request = async(reqParams: REQ_AJAX_PARAMS) => {
  // console.time(reqParams.url)

  // 在 request 入口缓存 Nuxt 上下文，避免 $fetch 拦截器异步回调中 composable 不可用
  const nuxtApp = useNuxtApp()
  const { $getCountry } = nuxtApp
  const route = useRoute()
  const router = useRouter()
  const clientCtx: RequestClientCtx = { route, router }

  const isMobile = useIsMobile().value
  const regionId = useCurrentCountry().value
  const regionIdHeader = useCookie('regionId').value || regionId.toString() || '6'
  const regionCodeHeader = useCookie('regionCode').value || useCurrentCountryCode().value || 'KE'
  const accessToken = useUserInfo().value?.accessToken
  const anonSidCookie = useCookie('anon_sid')
  let anonSid = anonSidCookie.value
  const isDevMode = route.query?.development === '1'

  const reg = /(\{siteCode\})/
  if(reg.test(reqParams.url)) {
    const siteCode = $getCountry()?.code || regionCodeHeader || 'KE'
    reqParams.url = reqParams.url.replace(new RegExp("\\{siteCode\\}","g"), siteCode)
  }

  const loading = useRequestLoading()
  if(reqParams.loading) {
    loading.value = true
    // toast = showLoadingToast({ message: 'loading', forbidClick: true, duration: 0 }) 
  }
  
  const fetch = $fetch.create({
    // 请求拦截器
    async onRequest({ options }) {
      if(!process.client && options.baseURL === domain.BASE_URL) {
        options.baseURL = domain.LOCAL_BASE_URL
      }
      if(process.client && options.baseURL === domain.LOCAL_BASE_URL) {
        options.baseURL = domain.BASE_URL
      }
      // if()
      
      options.headers = new Headers(options.headers || {})

      if(/image\.kilitest\.com/.test(reqParams.url)) {
        options.headers = new Headers({})
      } else {
        // 添加请求头,没登录不携带token
        options.headers = new Headers(options.headers)
        // 536 有代付，PC端没有代付功能
        options.headers.set('Pfx-AppVersion', isMobile ? '536' : '535')
        options.headers.set('Pfx-Platform', isMobile ? `Mobile-Web` : 'Web')
        options.headers.set('Pfx-Region', regionIdHeader)
        options.headers.set('Pfx-Region-Code', regionCodeHeader) // 后端永胜让添加
        // 暂时给Node服务器用
        // options.headers.set('Node-Region-Code', siteCode || 'KE')

        options.headers.set('Pfx-Language', 'en')
        
        if(accessToken) {
          if(/\/old_order_status/.test(reqParams.url)) {
            options.headers.set('AccessToken', accessToken)
          } else if(!/lipapay[-api]?|pay\.kilimall\.com/.test(reqParams.baseURL || reqParams.url)) { 
            options.headers.set('pfx-accesstoken', accessToken)
          }
        } else {
          // SSR / 客户端都要有 anon_sid：否则无 cookie 的首屏 SSR 不带 Pfx-Anon-Sid，
          // 后端限流会落到全局 anonymous（如 goods-detail 2/second）导致大量 429
          if (!anonSid) {
            anonSid = uuidv4()
            anonSidCookie.value = anonSid
          }
          options.headers.set('Pfx-Anon-Sid', anonSid)
        }
      }
      
      if(reqParams.headers) {
        Object.assign(options.headers, reqParams)
      }

      if(isDevMode){
        console.log('-- 调试模式 -- 请求日志输入： ', `${options.baseURL}${reqParams.url}`, JSON.stringify(options.headers || {}))
      }
    },
    // 响应拦截
    async onResponse({ response }) {
      // console.timeEnd(reqParams.url)
      if(getTypeof(response._data) === 'Blob') {
        return Promise.resolve(response._data)
      } else {
        if(response.status !== 200 && response.status !== 201) {
          return onResponseError(response, reqParams.toastError, reqParams.server, clientCtx, false, reqParams.url)
        } else {
          if (response.headers.get('content-disposition') || !response?._data){
            return response
          }
          
          if(!response._data.code) {
            // 成功返回
            return response?._data  
          }
          // 在这里判断错误
          if (![0, 200].includes(Number(response._data.code))) {
            if(reqParams.rejectBusinessError) {
              return onResponseError(response, reqParams.toastError, reqParams.server, clientCtx, true, reqParams.url)
            }
            handleResponseError(response, reqParams.toastError, clientCtx, false, reqParams.url)
          }
          // 成功返回
          return response?._data
        }
      }
    },
  })

  // localhost 表示本地，不需要加 BASE URL
  reqParams.baseURL = reqParams.baseURL || domain.BASE_URL

  const reqFn = reqParams.server
    ? useAsyncData(
        reqParams.url,
        () => fetch(reqParams.url, reqParams as any),
        { server: true }
      ).then((r: any) => {
        if(r.error.value && r.error.value?.statusCode !== 200) {
          const errData = r.error.value?.cause?.message

          if(r.error.value.data && typeof r.error.value.data === 'object') {
            return Promise.reject(r.error.value.data)
          }

          return Promise.reject(errData && /^[{\[].*[\]}]$/.test(errData) ? JSON.parse(errData) : errData)
        }

        return r.data?.value
      })
    : fetch(reqParams.url, reqParams as any)

  return reqFn.finally(() => { loading?.value && (loading.value = false) })
}

// 自动导出
export const $http: HTTP = {
  get: (reqParams: REQ_PARAMS) => {
    const req_params: REQ_AJAX_PARAMS = { ...reqParams, ...{method: 'GET'}}
    return request(req_params)
  },
  post: (reqParams: REQ_PARAMS) => {
    const req_params: REQ_AJAX_PARAMS = { ...reqParams, ...{method: 'POST'}}
    return request(req_params)
  },
  patch: (reqParams: REQ_PARAMS) => {
    const req_params: REQ_AJAX_PARAMS = { ...reqParams, ...{ method: 'PATCH' }}
    return request(req_params)
  },
  put: (reqParams: REQ_PARAMS) => {
    const req_params: REQ_AJAX_PARAMS = { ...reqParams, ...{method: 'PUT'}}
    return request(req_params)
  },
  delete: (reqParams: REQ_PARAMS) => {
    const req_params: REQ_AJAX_PARAMS = { ...reqParams, ...{method: 'DELETE'}}
    return request(req_params)
  },
  ajax: request
}
